import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ConfirmDialog, EmptyState, ErrorNote, Loading, Tabs } from '../components/ui';
import { errorMessage } from '../lib/api';
import { formatRelative } from '../lib/format';
import { NOTIFICATION_LABELS } from '../lib/notifications';
import { notificationsApi } from '../lib/resources';
import type { AppNotification } from '../types';

/** Notificações (Fase 6 / N2): todas ou não lidas, marcar e limpar. */
export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const [clearing, setClearing] = useState(false);
  const list = useQuery({
    queryKey: ['notifications', 'list', tab],
    queryFn: () => notificationsApi.list({ unread: tab === 'unread', take: 100 }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });
  const read = useMutation({ mutationFn: notificationsApi.read, onSuccess: refresh });
  const readAll = useMutation({ mutationFn: notificationsApi.readAll, onSuccess: refresh });
  const clear = useMutation({
    mutationFn: notificationsApi.clearRead,
    onSuccess: () => {
      setClearing(false);
      void refresh();
    },
  });

  const open = (notice: AppNotification) => {
    if (!notice.readAt) read.mutate(notice.id);
    if (notice.link) navigate(notice.link);
  };
  const data = list.data;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Notificações</h1>
          <p className="text-sm text-slate-500">
            {data
              ? `${data.unread} não lida(s)`
              : 'Avisos do que acontece com os seus leads, negócios e conversas.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={!data?.unread || readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            Marcar todas como lidas
          </Button>
          <Button onClick={() => setClearing(true)}>Limpar lidas</Button>
          <Link
            to="/configuracoes/notificacoes"
            className="self-center text-sm text-slate-600 hover:underline"
          >
            Preferências
          </Link>
        </div>
      </header>

      <Tabs
        tabs={[
          ['all', 'Todas'],
          ['unread', 'Não lidas'],
        ]}
        value={tab}
        onChange={setTab}
      />

      {list.isLoading ? (
        <Loading />
      ) : list.isError ? (
        <ErrorNote>{errorMessage(list.error)}</ErrorNote>
      ) : !data?.data.length ? (
        <EmptyState title={tab === 'unread' ? 'Nada por ler' : 'Nenhuma notificação'} />
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {data.data.map((notice) => (
            <li key={notice.id}>
              <button
                type="button"
                onClick={() => open(notice)}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 ${
                  notice.readAt ? '' : 'bg-sky-50/40'
                }`}
              >
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notice.readAt ? 'bg-transparent' : ''}`}
                  style={notice.readAt ? undefined : { backgroundColor: 'var(--crm-primary)' }}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-sm ${notice.readAt ? 'text-slate-600' : 'font-medium text-slate-900'}`}
                  >
                    {notice.title}
                  </span>
                  {notice.body && (
                    <span className="block truncate text-xs text-slate-500">{notice.body}</span>
                  )}
                  <span className="block text-[11px] text-slate-400">
                    {NOTIFICATION_LABELS[notice.type] ?? notice.type} ·{' '}
                    {formatRelative(notice.createdAt)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {clearing && (
        <ConfirmDialog
          title="Limpar notificações lidas"
          message="As notificações já lidas são apagadas. As não lidas ficam."
          confirmLabel="Limpar"
          busy={clear.isPending}
          error={clear.isError ? errorMessage(clear.error) : null}
          onConfirm={() => clear.mutate()}
          onClose={() => setClearing(false)}
        />
      )}
    </div>
  );
}
